import React, { useState, useMemo } from 'react';
import { Sparkles, Loader2, FileText, Check, Copy, RefreshCw } from 'lucide-react';

const AiContentOutline = ({ project, apiFetch, showNotification }) => {
  const pages = useMemo(() => {
    const summaries = project?.technical_audit?.page_summaries || [];
    return summaries.filter((p) => (p.content?.word_count || 0) < 300);
  }, [project]);

  const [selectedUrl, setSelectedUrl] = useState(pages[0]?.url || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);

  const current = pages.find((p) => p.url === selectedUrl) || pages[0];

  const handleGenerate = async () => {
    if (!current) return;
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        url: current.url,
        page_info: {
          title: current.page_info?.title,
          meta_description: current.page_info?.meta_description,
          h1_content: current.headings?.h1_content,
          word_count: current.content?.word_count,
          target_keyword: current.page_info?.title?.split('|')[0]?.trim() || '',
        },
      };
      const res = await apiFetch('/seo/content-outline', { method: 'POST', body: JSON.stringify(payload) });
      setResult(res);
      showNotification('Content expansion outline generated!', 'success');
    } catch (err) {
      showNotification('Could not generate outline: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(idx);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  if (!pages.length) return null;

  return (
    <div className="bg-gradient-to-br from-green-950/15 to-black/40 border border-green-500/20 rounded-2xl p-5 flex flex-col gap-4 animate-in fade-in duration-300">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-green-400" />
        <h4 className="font-extrabold text-white text-sm">Fix with AI — Content Gap & Expansion Outline</h4>
        <span className="text-[9px] text-green-300 font-mono bg-green-950/50 border border-green-500/25 px-2 py-0.5 rounded ml-auto">
          {pages.length} thin pages (&lt;300 words)
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select
          value={selectedUrl}
          onChange={(e) => { setSelectedUrl(e.target.value); setResult(null); }}
          className="bg-black/60 border border-gray-700 text-blue-300 font-mono text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-green-500 max-w-full truncate cursor-pointer font-semibold shadow-inner md:col-span-2"
        >
          {pages.map((p, idx) => (
            <option key={idx} value={p.url}>
              📄 {p.url} ({p.content?.word_count} words)
            </option>
          ))}
        </select>

        <button
          onClick={handleGenerate}
          disabled={loading}
          className="bg-green-900/40 hover:bg-green-900/60 border border-green-500/30 text-green-200 font-bold px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all text-xs disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          {loading ? 'Generating...' : 'Generate Outline'}
        </button>
      </div>

      {loading && (
        <div className="bg-black/50 p-6 rounded-lg border border-green-500/20 text-center animate-pulse text-green-300 font-mono text-[11px]">
          ⚙️ Mapping sub-topics and long-tail gaps for expansion...
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-4 animate-in fade-in duration-300">
          <div className="flex flex-col gap-2.5">
            {result.outline?.map((section, idx) => (
              <div key={idx} className="bg-gray-950/80 border border-gray-850 rounded-xl p-3.5 flex flex-col gap-2">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-xs font-bold text-green-300 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-900/40 border border-green-500/30 text-green-300 flex items-center justify-center text-[9px] font-mono shrink-0">
                      {idx + 1}
                    </span>
                    {section.section}
                  </span>
                  <button
                    onClick={() => handleCopy(`${section.section}\n${(section.key_points || []).map((p) => `- ${p}`).join('\n')}`, idx)}
                    className="text-[9px] text-gray-400 hover:text-white flex items-center gap-1 bg-gray-850 px-2 py-0.5 rounded border border-gray-800 transition shrink-0"
                  >
                    {copiedSection === idx ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
                    {copiedSection === idx ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <ul className="flex flex-col gap-1 pl-7">
                  {(section.key_points || []).map((point, pIdx) => (
                    <li key={pIdx} className="text-[11px] text-gray-400 flex items-start gap-1.5">
                      <span className="text-green-500 mt-0.5 shrink-0">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {result.suggested_keywords?.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Suggested Long-Tail Keywords</span>
              <div className="flex flex-wrap gap-2">
                {result.suggested_keywords.map((kw, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full bg-purple-950/50 text-purple-300 border border-purple-500/20 text-xs font-mono font-medium">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.rationale && (
            <p className="text-xs text-gray-400 italic bg-green-950/15 border border-green-500/10 p-2.5 rounded-lg leading-relaxed">
              {result.rationale}
            </p>
          )}

          <button
            onClick={handleGenerate}
            className="text-[10px] text-green-300 hover:text-green-200 flex items-center gap-1.5 justify-center bg-green-950/30 border border-green-500/20 rounded-lg py-2 transition"
          >
            <RefreshCw size={11} /> Regenerate
          </button>
        </div>
      )}
    </div>
  );
};

export default AiContentOutline;
